/**
 * The saved sheet's ROUNDING on REOPEN.
 *
 * G6 reopens by re-merging the template from the tables (owner rule 2026-09-09); OG reopens its saved sheet. A Save
 * with ApplyRoundingOnsave (or the Apply Rounding button) wraps each Result / %Recovery / RPD cell in
 * `_LIMSRound(<expr>,"sig","cut","dec","rpt","def")` and sets the rounded value's number format — the TABLES then
 * hold the rounded value (SampleInfo %Recovery 107), but the template the re-merge starts from has no wrap, so the
 * reopened sheet showed the raw 106.7 where OG shows 107 (PFAS QB26100101_001 vs QB26100102_001, validated batch,
 * 2026-10-01). The wrap and its format live ONLY in the saved sheet, so they are carried from there — the same rule
 * as the blob-only values (owner decision D2, 2026-09-26). The cell's own expression stays the re-merge's (tables
 * and template still win); only the rounding around it, and its format, follow the saved sheet.
 *
 * A band cell takes the saved wrap when: its row matches by SysSampleCode | Parameter | RunNo (never position), the
 * column caption above the band reads the same in the template and the saved sheet (drift guard), the saved cell is
 * a `_LIMSRound(...)` with its settings arguments, and the re-merged cell is a formula not already wrapped or a
 * numeric value. Derived from the saved sheet, never a per-template list of columns.
 */

import {
  SKIP_SHEET_RE, MERGE_EXCLUDED_SHEET_RE, findBand, bandRows, text, normCaption,
} from './sdmsBlobOnlyValues';
import { loadSdmsWorkbook } from './sdmsWorkbookIo';

type Ws = import('exceljs').Worksheet;
type Cell = import('exceljs').Cell;

const WRAP_RE = /^\s*=?\s*_LIMSRound\s*\(/i;
const NUMERIC_TEXT_RE = /^\s*[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?\s*$/;

/** A cell's formula text (a shared-formula clone translated to its own row), or null. */
function formulaText(cell: Cell): string | null {
  const v = cell.value as { formula?: string; sharedFormula?: string } | null;
  if (!v || typeof v !== 'object' || !('formula' in v || 'sharedFormula' in v)) return null;
  const f = typeof cell.formula === 'string' && cell.formula ? cell.formula : v.formula;
  return f ? f.replace(/^\s*=/, '') : null;
}

/** The settings arguments of a `_LIMSRound(<expr>, a1..a5)` formula, as written (quotes kept), or null. */
export function limsRoundSettingsArgs(formula: string): string[] | null {
  if (!WRAP_RE.test(formula)) return null;
  const open = formula.indexOf('(');
  const body = formula.slice(open + 1);
  const args: string[] = [];
  let depth = 0, inStr = false, start = 0, end = -1;
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (inStr) { if (ch === '"') { if (body[i + 1] === '"') i++; else inStr = false; } continue; }
    if (ch === '"') inStr = true;
    else if (ch === '(') depth++;
    else if (ch === ')') { if (depth === 0) { end = i; break; } depth--; }
    else if (ch === ',' && depth === 0) { args.push(body.slice(start, i)); start = i + 1; }
  }
  if (end < 0 || body.slice(end + 1).trim() !== '') return null;
  args.push(body.slice(start, end));
  if (args.length < 6) return null;   // <expr> + sig, cut, dec, rpt, def
  return args.slice(-5).map(a => a.trim());
}

/** Carry the rounding of one sheet; returns how many cells were wrapped. */
function carrySheet(tpl: Ws, cur: Ws, saved: Ws): number {
  const band = findBand(tpl);
  if (!band) return 0;
  const capRow = band.row - 1;
  const lastCol = Math.max(band.lastCol, saved.columnCount);
  const cols: number[] = [];
  for (let c = 1; c <= lastCol; c++) {
    const cap = capRow >= 1 ? normCaption(text(tpl.getRow(capRow).getCell(c))) : '';
    if (cap !== (capRow >= 1 ? normCaption(text(saved.getRow(capRow).getCell(c))) : '')) continue;   // drift guard
    cols.push(c);
  }
  if (!cols.length) return 0;
  const savedRows = bandRows(saved, band, false);
  let n = 0;
  for (const [key, cr] of bandRows(cur, band, false)) {
    const sr = savedRows.get(key);
    if (!sr) continue;
    for (const c of cols) {
      const sc = saved.getRow(sr).getCell(c);
      const sf = formulaText(sc);
      const args = sf ? limsRoundSettingsArgs(sf) : null;
      if (!args) continue;
      const cc = cur.findRow(cr)?.findCell(c);   // look only: never create a cell the merge did not write
      if (!cc) continue;
      const cv = cc.value as { shareType?: string } | number | string | null;
      if (cv && typeof cv === 'object' && cv.shareType === 'shared') continue;   // a shared master: its clones read it
      const cf = formulaText(cc);
      let inner: string;
      if (cf !== null) {
        if (WRAP_RE.test(cf)) continue;                                           // already rounded
        inner = cf;
      } else if (typeof cv === 'number' && Number.isFinite(cv)) inner = String(cv);
      else if (typeof cv === 'string' && NUMERIC_TEXT_RE.test(cv)) inner = String(Number(cv));
      else continue;                                                              // nothing numeric to round
      cc.value = { formula: `_LIMSRound(${inner},${args.join(',')})` } as never;
      // A loaded workbook shares one style object between cells: setting numFmt on it would re-format every cell
      // sharing that style, so the cell gets its own copy.
      if (sc.numFmt) cc.style = { ...cc.style, numFmt: sc.numFmt };
      n++;
    }
  }
  return n;
}

/**
 * Carry the saved sheet's `_LIMSRound` wraps (and their number formats) onto the re-merged workbook `cur`, per the
 * rules above. `templateBuf` is the batch's template, `model` its Model (1 = one sheet per run, read against the
 * template's first sheet). Returns how many cells were wrapped (0 = `cur` untouched).
 */
export async function carrySavedRoundingIntoWorkbook(
  cur: import('exceljs').Workbook, templateBuf: ArrayBuffer, savedBuf: ArrayBuffer, model = 0,
  /** The template and saved sheet already read (the open chain shares them with the blob-only values). */
  parsed?: { tpl: import('exceljs').Workbook; saved: import('exceljs').Workbook },
): Promise<number> {
  const [tpl, saved] = parsed ? [parsed.tpl, parsed.saved]
    : await Promise.all([loadSdmsWorkbook(templateBuf), loadSdmsWorkbook(savedBuf)]);
  const tplByName = new Map(tpl.worksheets.map(w => [w.name.toLowerCase(), w]));
  const main = tpl.worksheets[0];
  let n = 0;
  for (const ws of cur.worksheets) {
    const name = ws.name.trim();
    if (SKIP_SHEET_RE.test(name) || MERGE_EXCLUDED_SHEET_RE.test(name)) continue;
    const sv = saved.worksheets.find(w => w.name.toLowerCase() === ws.name.toLowerCase());
    const tw = tplByName.get(ws.name.toLowerCase()) ?? (model === 1 ? main : undefined);
    if (!sv || !tw) continue;
    n += carrySheet(tw, ws, sv);
  }
  return n;
}
